import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState, type ChangeEvent } from "react";

import { Logo, PAGE_CONTAINER } from "../components/site";
import { useTaal } from "../lib/taal";
import { vertaal, type TaalCode } from "../lib/vertalingen";
import { zoekAdres } from "../lib/adres.functions";
import { SITE } from "../lib/seo-data";
import { analyseerKlus, type KlusAnalyse } from "../lib/klus-analyse.functions";
import { submitAanvraag } from "../lib/leads.functions";
import { trackSubmittedLead } from "../lib/analytics";
import { trackMetaLead } from "../lib/meta-pixel";
import { stuurVerificatieCode, controleerVerificatieCode } from "../lib/smsverificatie.functions";
import { bewaarPoging } from "../lib/statistiek.functions";



function leesAlsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Foto kon niet gelezen worden"));
    reader.readAsDataURL(file);
  });
}

export const Route = createFileRoute("/aanvraag")({
  head: () => ({
    meta: [
      { title: "Offertes aanvragen - gratis & vrijblijvend | offerto" },
      {
        name: "description",
        content:
          "Vul in 1 minuut je aanvraag in: locatie, type opdracht, planning en projectomschrijving met foto's. Ontvang gratis offertes van erkende elektriciens.",
      },
      { property: "og:title", content: "Offertes aanvragen - gratis & vrijblijvend" },
      {
        property: "og:description",
        content: "Vul je aanvraag in en ontvang gratis offertes van erkende elektriciens.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}/aanvraag` },
      { property: "og:locale", content: "nl_NL" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}/aanvraag` }],
  }),
  validateSearch: (search: Record<string, unknown>): { postcode?: string; klus?: number; huisnummer?: string } => {
    const p = search["postcode"];
    const hRaw = search["huisnummer"];
    // De router json-codeert puur cijferige strings ("12" -> "\"12\""); haal die aanhalingstekens weg.
    const h = typeof hRaw === "string" && hRaw.startsWith('"') && hRaw.endsWith('"') ? hRaw.slice(1, -1) : hRaw;
    const rawKlus = search["klus"];
    const klus = typeof rawKlus === "number" ? rawKlus : typeof rawKlus === "string" && rawKlus !== "" ? Number(rawKlus) : NaN;
    return {
      ...(typeof p === "string" && p !== "" ? { postcode: p } : {}),
      ...(typeof h === "string" && h.trim() !== "" ? { huisnummer: h.trim().slice(0, 12) } : {}),
      ...(Number.isInteger(klus) && klus >= 0 && klus < vertaal("nl", "klusOpties").length ? { klus } : {}),
    };
  },
  component: Aanvraag,
});

const TOTAL = 9;

function TrustBar() {
  const { t } = useTaal();
  return (
    <div className="wizard-trust mt-6 shrink-0 border-t border-border bg-secondary/60">
      <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-center gap-x-10 gap-y-3 px-4 py-5 text-sm text-muted-foreground">
        <span>
          {t("trustLaatsteA")} <strong className="text-foreground">{t("trustLaatsteB")}</strong>
        </span>
        <span>
          {t("trustVandaagA")} <strong className="text-foreground">{t("trustVandaagB")}</strong>
        </span>
        <span>
          <strong className="text-foreground">{t("trustSsl")}</strong> {t("trustSslTekst")}
        </span>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  const ok = value.trim().length > 1;
  return (
    <label className="block">
      <span className="text-sm font-bold">{label}</span>
      <span className="mt-2 flex items-center gap-3">
        <input
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={`h-12 w-full rounded-md border px-4 text-base outline-none transition-colors ${
            ok ? "border-primary" : "border-input"
          } focus:border-primary`}
        />
        <span
          className={`flex size-8 shrink-0 items-center justify-center rounded-full text-base font-bold transition-all duration-200 ${
            ok
              ? "bg-success text-success-foreground scale-100"
              : "bg-muted text-muted-foreground/40 scale-90"
          }`}
          aria-hidden={!ok}
        >
          {ok ? "✓" : "○"}
        </span>
      </span>
    </label>
  );
}

function Radios({
  options,
  value,
  onChange,
  labels,
}: {
  options: readonly string[];
  value: string;
  onChange: (v: string) => void;
  labels?: Record<string, string>;
}) {
  return (
    <div className="mt-4 space-y-1">
      {options.map((o) => (
        <label key={o} className="flex cursor-pointer items-center gap-3 rounded-md py-2 pr-3">
          <input
            type="radio"
            checked={value === o}
            onChange={() => onChange(o)}
            className="h-5 w-5 accent-[var(--color-primary)]"
          />
          <span className={value === o ? "font-bold" : ""}>{labels?.[o] ?? o}</span>
        </label>
      ))}
    </div>
  );
}

function Aanvraag() {
  const { t, taal } = useTaal();
  const TYPE_OPDRACHT = t("wizardTypeOpties");
  const gekozenKlus = Route.useSearch().klus;
  const geselecteerdeKlus = gekozenKlus === undefined ? "" : (t("klusOpties")[gekozenKlus] ?? "");
  const typeOpties = geselecteerdeKlus && !TYPE_OPDRACHT.includes(geselecteerdeKlus)
    ? [geselecteerdeKlus, ...TYPE_OPDRACHT] : TYPE_OPDRACHT;
  const SOORT = t("wizardSoortOpties");
  const UITVOERDATUM = t("wizardDatumOpties");
  const INTENTIE = t("wizardIntentieOpties");
  const zoek = Route.useSearch();
  const [step, setStep] = useState(0);
  const [d, setD] = useState({
    postcode: typeof zoek.postcode === "string" ? zoek.postcode : "",
    plaats: "",
    straat: "",
    huisnummer: typeof zoek.huisnummer === "string" ? zoek.huisnummer : "",
    type: geselecteerdeKlus,
    soort: "",
    datum: "",
    intentie: "",
    omschrijving: "",
    voornaam: "",
    achternaam: "",
    telefoon: "",
    email: "",
    code: "",
  });
  const vorigeTaal = useRef<TaalCode>(taal);
  useEffect(() => {
    if (vorigeTaal.current === taal) return;
    const vorig = vorigeTaal.current;
    vorigeTaal.current = taal;
    setD((huidig) => {
      const vorigeOpties = [...vertaal(vorig, "klusOpties"), ...vertaal(vorig, "wizardTypeOpties")];
      const nieuweOpties = [...vertaal(taal, "klusOpties"), ...vertaal(taal, "wizardTypeOpties")];
      const positie = vorigeOpties.indexOf(huidig.type);
      return positie < 0 ? huidig : { ...huidig, type: nieuweOpties[positie] ?? huidig.type };
    });
  }, [taal]);
  const [fotos, setFotos] = useState<{ name: string; url: string }[]>([]);
  const [analyse, setAnalyse] = useState<KlusAnalyse | null>(null);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState("");
  const [versturen, setVersturen] = useState(false);
  const [verstuurFout, setVerstuurFout] = useState("");
  const set = (k: keyof typeof d) => (v: string) => setD((p) => ({ ...p, [k]: v }));
  const analyseer = useServerFn(analyseerKlus);
  const verstuur = useServerFn(submitAanvraag);
  const stuurCode = useServerFn(stuurVerificatieCode);
  const checkCode = useServerFn(controleerVerificatieCode);
  const [smsMelding, setSmsMelding] = useState("");
  const [codeKanaal, setCodeKanaal] = useState<"sms" | "email">("sms");
  const bewaar = useServerFn(bewaarPoging);
  const pogingId = useRef<string | null>(null);
  const dRef = useRef(d);
  dRef.current = d;
  function meldPoging(stap: number, voltooid = false, leadId?: string) {
    if (!pogingId.current) pogingId.current = crypto.randomUUID();
    const g = dRef.current;
    void bewaar({
      data: {
        id: pogingId.current, stap, voltooid, ...(leadId ? { leadId } : {}),
        gegevens: {
          postcode: g.postcode, plaats: g.plaats, straat: g.straat, huisnummer: g.huisnummer,
          type_opdracht: g.type, soort_aanvraag: g.soort, uitvoerdatum: g.datum, intentie: g.intentie,
          omschrijving: g.omschrijving.slice(0, 5000), voornaam: g.voornaam, achternaam: g.achternaam,
          telefoon: g.telefoon, email: g.email,
        },
      },
    }).catch(() => undefined);
  }
  useEffect(() => {
    if (step >= 1) meldPoging(step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  const [smsBezig, setSmsBezig] = useState(false);
  const lookup = useServerFn(zoekAdres);

  const lookupRef = useRef(lookup);
  lookupRef.current = lookup;
  const [adresMelding, setAdresMelding] = useState("");
  const [adresBezig, setAdresBezig] = useState(false);
  const adresTeller = useRef(0);
  const inhoudRef = useRef<HTMLDivElement>(null);
  const normaleHoogte = useRef(0);
  const [toetsenbord, setToetsenbord] = useState<{ hoogte: number; boven: number } | null>(null);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    normaleHoogte.current = Math.max(window.innerHeight, viewport.height);
    let timer: ReturnType<typeof setTimeout> | undefined;
    const update = () => {
      if (document.activeElement instanceof HTMLElement &&
          document.activeElement.matches("input:not([type=radio]):not([type=file]), textarea")) {
        const verschil = normaleHoogte.current - viewport.height;
        setToetsenbord(verschil > 120 ? { hoogte: viewport.height, boven: viewport.offsetTop } : null);
        if (verschil > 120) {
          if (timer) clearTimeout(timer);
          timer = setTimeout(() => {
            const veld = document.activeElement;
            const inhoud = inhoudRef.current;
            if (!(veld instanceof HTMLElement) || !inhoud || !inhoud.contains(veld)) return;
            const veldRect = veld.getBoundingClientRect();
            const inhoudRect = inhoud.getBoundingClientRect();
            if (veldRect.bottom > inhoudRect.bottom - 16) inhoud.scrollTop += veldRect.bottom - inhoudRect.bottom + 16;
            else if (veldRect.top < inhoudRect.top + 16) inhoud.scrollTop -= inhoudRect.top + 16 - veldRect.top;
          }, 80);
        }
      } else {
        setToetsenbord(null);
        normaleHoogte.current = Math.max(window.innerHeight, viewport.height);
      }
    };
    const onBlur = () => { timer = setTimeout(update, 150); };
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    document.addEventListener("focusin", update);
    document.addEventListener("focusout", onBlur);
    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", onBlur);
      if (timer) clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const postcode = d.postcode.trim();
    const huisnummer = d.huisnummer.trim();
    if (postcode.replace(/\s/g, "").length < 6 || huisnummer === "") {
      setAdresMelding("");
      setAdresBezig(false);
      return;
    }
    const beurt = ++adresTeller.current;
    const timer = setTimeout(() => {
      setAdresBezig(true);
      void lookupRef
        .current({ data: { postcode, huisnummer } })
        .then((res) => {
          if (beurt !== adresTeller.current) return;
          setAdresMelding(res.melding);
          if (res.gevonden) {
            setD((p) => ({ ...p, straat: res.straat, plaats: res.plaats, postcode: res.postcode }));
          }
        })
        .catch(() => {
          if (beurt === adresTeller.current) setAdresMelding(t("wizardAdresFout"));
        })
        .finally(() => {
          if (beurt === adresTeller.current) setAdresBezig(false);
        });
    }, 500);
    return () => clearTimeout(timer);
  }, [d.postcode, d.huisnummer]);



  async function verstuurAanvraag() {
    setVersturen(true);
    setVerstuurFout("");
    try {
      const resultaat = await verstuur({
        data: {
          postcode: d.postcode.trim(),
          plaats: d.plaats.trim(),
          straat: d.straat.trim(),
          huisnummer: d.huisnummer.trim() || null,
          type_opdracht: d.type || null,
          soort_aanvraag: d.soort || null,
          uitvoerdatum: d.datum || null,
          intentie: d.intentie || null,
          omschrijving: d.omschrijving.trim() || null,
          fotos,
          ai_samenvatting: analyse?.samenvatting ?? null,
          ai_categorie: analyse?.categorie ?? null,
          ai_aandachtspunten: analyse?.aandachtspunten ?? [],
          voornaam: d.voornaam.trim(),
          achternaam: d.achternaam.trim(),
          telefoon: d.telefoon.trim(),
          email: d.email.trim(),
        },
      });
      if (!resultaat.ok) throw new Error("Aanvraag niet opgeslagen");
      trackSubmittedLead(resultaat.id);
      trackMetaLead(resultaat.id);
      meldPoging(9, true, resultaat.id);
      // Conversie meten: aanvraag is succesvol verstuurd.
      (window as unknown as { oaiq?: (...args: unknown[]) => void }).oaiq?.(
        "measure",
        "lead_created",
        { type: "customer_action" },
      );
      setStep(TOTAL);
    } catch {
      setVerstuurFout(t("wizardVerstuurFout"));
    } finally {
      setVersturen(false);
    }
  }

  async function vraagCodeAan(): Promise<false | "code" | "overslaan"> {
    setSmsBezig(true);
    setVerstuurFout("");
    try {
      const res = await stuurCode({
        data: { telefoon: d.telefoon.trim(), email: d.email.trim(), achternaam: d.achternaam.trim() },
      });
      setSmsMelding(res.melding);
      if (res.kanaal) setCodeKanaal(res.kanaal);
      if (!res.verzonden) {
        setVerstuurFout(res.melding);
        return false;
      }
      return res.overslaan ? "overslaan" : "code";
    } catch {
      setSmsMelding(t("wizardSmsFout"));
      setVerstuurFout(t("wizardSmsFout"));
      return false;
    } finally {
      setSmsBezig(false);
    }
  }

  function onVolgende() {
    if (step === 7) {
      void (async () => {
        const res = await vraagCodeAan();
        if (res === "overslaan") {
          await verstuurAanvraag();
        } else if (res === "code") {
          setD((p) => ({ ...p, code: "" }));
          setStep(8);
        }
      })();
      return;
    }
    if (step === 8) {
      void (async () => {
        setVersturen(true);
        setVerstuurFout("");
        try {
          const res = await checkCode({ data: { telefoon: d.telefoon.trim(), code: d.code.trim() } });
          if (!res.geldig) {
            setVerstuurFout(res.melding);
            return;
          }
        } catch {
          setVerstuurFout(t("wizardCodeFout"));
          return;
        } finally {
          setVersturen(false);
        }
        await verstuurAanvraag();
      })();
      return;
    }
    setStep((s) => s + 1);
  }


  async function onFiles(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    const nieuw = await Promise.all(
      files.map(async (f) => ({ name: f.name, url: await leesAlsDataUrl(f) })),
    );
    setFotos((p) => [...p, ...nieuw]);
  }

  async function onAnalyseer() {
    setBezig(true);
    setFout("");
    try {
      const res = await analyseer({
        data: { omschrijving: d.omschrijving, fotos: fotos.slice(0, 4).map((f) => f.url) },
      });
      setAnalyse(res);
      if (res.categorie) setD((p) => ({ ...p, type: res.categorie }));
    } catch (error) {
      setFout(error instanceof Error ? error.message : t("wizardAiFout"));
    } finally {
      setBezig(false);
    }
  }

  const canNext = [
    d.postcode.trim() !== "" && d.plaats.trim() !== "" && d.straat.trim() !== "",
    d.type !== "",
    d.soort !== "",
    d.datum !== "",
    d.intentie !== "",
    true,
    d.voornaam.trim() !== "" && d.achternaam.trim() !== "",
    d.telefoon.trim() !== "" && d.email.trim() !== "",
    d.code.trim().length === 4,
  ][step];

  const progress = ((step + 1) / TOTAL) * 100;

  return (
    <div
      className={`flex h-dvh w-full flex-col overflow-hidden overscroll-none bg-background ${toetsenbord ? "fixed left-0 z-50" : ""}`}
      style={toetsenbord ? { height: toetsenbord.hoogte, top: toetsenbord.boven } : undefined}
    >
      <header className="border-b border-border">
        <div className={`${PAGE_CONTAINER} py-5`}>
          <Logo />
        </div>
      </header>

      <main className="wizard-main mx-auto flex min-h-0 w-full max-w-2xl flex-1 flex-col px-4 py-6">
        <div className="h-1.5 w-full rounded-full bg-secondary">
          <div
            className="h-1.5 rounded-full bg-[oklch(0.42_0.08_250)] transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div ref={inhoudRef} className="wizard-content mt-6 min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {step === 0 && (
            <>
              <h1 className="text-2xl font-bold">{t("wizardLocatieTitel")}</h1>
              <div className="mt-6 space-y-5">
                <Field label={t("postcode")} value={d.postcode} onChange={set("postcode")} />
                <Field label={t("wizardHuisnummer")} value={d.huisnummer} onChange={set("huisnummer")} />
                {adresBezig && <p className="text-sm text-muted-foreground">{t("wizardAdresControleren")}</p>}
                {!adresBezig && adresMelding !== "" && (
                  <p className="text-sm font-medium text-destructive">{adresMelding}</p>
                )}
                <Field label={t("wizardPlaats")} value={d.plaats} onChange={set("plaats")} />
                <Field label={t("wizardStraat")} value={d.straat} onChange={set("straat")} />
              </div>

            </>
          )}

          {step === 1 && (
            <>
              <h1 className="text-2xl font-bold">{t("wizardTypeTitel")}</h1>
              <Radios options={typeOpties} value={d.type} onChange={set("type")} labels={t("klusLabels")} />
            </>
          )}

          {step === 2 && (
            <>
              <h1 className="text-2xl font-bold">{t("wizardSoortTitel")}</h1>
              <Radios options={SOORT} value={d.soort} onChange={set("soort")} />
            </>
          )}

          {step === 3 && (
            <>
              <h1 className="text-2xl font-bold">{t("wizardDatumTitel")}</h1>
              <Radios options={UITVOERDATUM} value={d.datum} onChange={set("datum")} />
            </>
          )}

          {step === 4 && (
            <>
              <h1 className="text-2xl font-bold">{t("wizardIntentieTitel")}</h1>
              <Radios options={INTENTIE} value={d.intentie} onChange={set("intentie")} />
            </>
          )}

          {step === 5 && (
            <>
              <h1 className="text-2xl font-bold">{t("wizardExtraTitel")}</h1>
              <p className="mt-1 text-muted-foreground">{t("wizardExtraIntro")}</p>
              <label htmlFor="omschrijving" className="mt-4 block text-sm font-bold">
                {t("wizardOmschrijvingLabel")}
              </label>
              <textarea
                id="omschrijving"
                aria-label={t("wizardOmschrijvingLabel")}
                value={d.omschrijving}
                onChange={(e) => set("omschrijving")(e.target.value)}
                rows={7}
                placeholder={(() => {
                  const i = TYPE_OPDRACHT.indexOf(d.type);
                  return t("wizardOmschrijvingPlaceholders")[i] ?? t("wizardOmschrijvingPlaceholder");
                })()}
                className="mt-4 w-full rounded-md border border-input bg-background p-4 text-base outline-none focus:border-primary"
              />

              <div className="mt-6">
                <h2 className="text-sm font-bold">{t("wizardFotosTitel")}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{t("wizardFotosTekst")}</p>
                <label className="mt-3 flex cursor-pointer items-center justify-center rounded-md border-2 border-dashed border-input px-4 py-8 text-sm text-muted-foreground hover:border-primary">
                  <input type="file" accept="image/*" multiple onChange={onFiles} className="hidden" />
                  {t("wizardFotosKiezen")}
                </label>
                {fotos.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4">
                    {fotos.map((f, i) => (
                      <div key={f.url} className="relative">
                        <img
                          src={f.url}
                          alt={`Foto ${i + 1} van ${fotos.length} van de situatie bij je klus`}
                          loading="lazy"
                          className="aspect-square w-full rounded-md object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => setFotos((p) => p.filter((_, x) => x !== i))}
                          className="absolute right-1 top-1 rounded-full bg-foreground/80 px-2 text-xs font-bold text-background"
                          aria-label={t("wizardFotoVerwijderen")}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-6 rounded-lg border border-border bg-secondary/40 p-5">
                <h2 className="text-sm font-bold">{t("wizardAiTitel")}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{t("wizardAiTekst")}</p>
                <button
                  type="button"
                  onClick={() => void onAnalyseer()}
                  disabled={bezig || (d.omschrijving.trim().length < 5 && fotos.length === 0)}
                  className="mt-4 rounded-md bg-accent px-6 py-3 font-bold text-accent-foreground transition-colors hover:bg-accent-hover disabled:opacity-40"
                >
                  {bezig ? t("wizardAiBezig") : t("wizardAiKnop")}
                </button>
                {fout && <p className="mt-3 text-sm font-medium text-destructive">{fout}</p>}
                {analyse && (
                  <div className="mt-5 rounded-md border border-border bg-background p-4">
                    <h3 className="text-sm font-bold">{t("wizardSamenvatting")}</h3>
                    <p className="mt-1 text-sm">{analyse.samenvatting}</p>
                    {analyse.categorie && (
                      <p className="mt-3 text-sm">
                        <span className="font-bold">{t("wizardCategorie")}</span> {analyse.categorie}{" "}
                        <span className="text-muted-foreground">{t("wizardCategorieHint")}</span>
                      </p>
                    )}
                    {analyse.aandachtspunten.length > 0 && (
                      <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                        {analyse.aandachtspunten.map((a) => (
                          <li key={a}>{a}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {step === 6 && (
            <>
              <div className="rounded-md bg-success/10 px-5 py-4 text-sm font-medium text-foreground">
...
                <span className="font-bold text-success">✓</span>
              </div>
              <h1 className="mt-6 text-2xl font-bold">{t("wizardNaamTitel")}</h1>
              <div className="mt-6 space-y-5">
                <Field label={t("wizardVoornaam")} value={d.voornaam} onChange={set("voornaam")} />
                <Field label={t("wizardAchternaam")} value={d.achternaam} onChange={set("achternaam")} />
              </div>
            </>
          )}

          {step === 7 && (
            <>
              <h1 className="text-2xl font-bold">{t("wizardContactTitel")}</h1>
              <p className="mt-2 text-muted-foreground">{t("wizardContactIntro")}</p>
              <div className="mt-6 space-y-5">
                <Field label={t("wizardTelefoon")} type="tel" value={d.telefoon} onChange={set("telefoon")} />
                <Field label={t("wizardEmail")} type="email" value={d.email} onChange={set("email")} />
              </div>
            </>
          )}

          {step === 8 && (
            <>
              <h1 className="text-3xl font-extrabold">{t("wizardVerifieerTitel")}</h1>
              <div className="mt-6 rounded-lg bg-[oklch(0.93_0.03_230)] p-6">
                <h2 className="text-xl font-bold">{t("wizardCodeTitel")}</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  {smsMelding !== "" ? (
                    smsMelding
                  ) : (
                    <>
                      {t("wizardCodeVerstuurdA")} <strong>SMS</strong> {t("wizardCodeVerstuurdB")}{" "}
                      <strong>{d.telefoon || t("wizardJeTelefoonnummer")}</strong>.
                    </>
                  )}
                </p>
                <label htmlFor="sms-code" className="mt-4 block text-sm font-bold">
                  {codeKanaal === "email" ? t("wizardCodeLabelEmail") : t("wizardCodeLabel")}
                </label>
                <input
                  id="sms-code"
                  aria-label={t("wizardCodeLabel")}
                  value={d.code}
                  onChange={(e) => set("code")(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder={t("wizardCodePlaceholder")}
                  inputMode="numeric"
                  className="mt-4 h-12 w-48 rounded-md border border-input bg-background px-4 text-base outline-none focus:border-primary"
                />
                <p className="mt-3 text-sm text-muted-foreground">
                  {t("wizardGeenCode")}{" "}
                  <button
                    type="button"
                    onClick={() => void vraagCodeAan()}
                    disabled={smsBezig}
                    className="font-bold text-primary underline disabled:opacity-50"
                  >
                    {smsBezig ? t("wizardOpnieuwBezig") : t("wizardOpnieuw")}
                  </button>
                </p>
              </div>
            </>
          )}


          {step === TOTAL && (
            <div className="text-center">
              <h1 className="text-3xl font-extrabold">{t("wizardKlaarTitel")}</h1>
              <p className="mt-3 text-muted-foreground">
                {t("wizardKlaarTekstA")} {d.plaats || t("wizardJeRegio")}. {t("wizardKlaarTekstB")}{" "}
                {d.email || t("wizardJeEmail")}.
              </p>
              <div className="mx-auto mt-8 max-w-xl space-y-4 text-left">
                <div className="rounded-lg border border-border bg-card p-5">
                  <h2 className="text-sm font-bold">{t("wizardAccountTitel")}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">{t("wizardAccountTekst")}</p>
                </div>
                <div className="rounded-lg border border-border bg-secondary/40 p-5">
                  <h2 className="text-sm font-bold">{t("wizardJouwOmschrijving")}</h2>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                    {d.omschrijving.trim() || t("wizardGeenOmschrijving")}
                  </p>
                  {fotos.length > 0 && (
                    <p className="mt-2 text-sm text-muted-foreground">
                      {fotos.length}{" "}
                      {fotos.length > 1 ? t("wizardFotosMeegestuurd") : t("wizardFotoMeegestuurd")}
                    </p>
                  )}
                  <p className="mt-2 text-sm text-muted-foreground">
                    <span className="font-bold text-foreground">{t("wizardTypeOpdracht")}</span>{" "}
                    {d.type || "-"}
                  </p>
                </div>
                {analyse && (
                  <div className="rounded-lg border border-border bg-background p-5">
                    <h2 className="text-sm font-bold">{t("wizardAiSamenvatting")}</h2>
                    <p className="mt-1 text-sm text-muted-foreground">{analyse.samenvatting}</p>
                  </div>
                )}
              </div>
              <Link
                to="/"
                className="mt-8 inline-block rounded-md bg-accent px-6 py-3 font-bold text-accent-foreground transition-colors hover:bg-accent-hover"
              >
                {t("wizardTerug")}
              </Link>
            </div>
          )}
        </div>

        {step < TOTAL && (
          <>
            <hr className="wizard-divider my-6 border-border" />
            <div className="flex min-w-0 items-start justify-between gap-2">
              <button
                type="button"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className="shrink-0 rounded-md border border-primary px-4 py-3 font-bold text-primary disabled:opacity-40 sm:px-6"
              >
                {t("wizardVorige")}
              </button>
              <div className="min-w-0 text-right">
                <button
                  type="button"
                  disabled={!canNext || versturen || smsBezig}
                  onClick={onVolgende}
                  className="rounded-md bg-accent px-4 py-3 font-bold text-accent-foreground transition-colors hover:bg-accent-hover disabled:opacity-40 sm:px-8"
                >
                  {versturen
                    ? t("wizardVersturen")
                    : step === 7
                      ? t("wizardOntvangOffertes")
                      : step === 8
                        ? t("wizardVerifieren")
                        : t("wizardVolgende")}{" "}
                  →
                </button>
                {verstuurFout && (
                  <p className="mt-2 text-sm font-medium text-destructive">{verstuurFout}</p>
                )}
              </div>
            </div>
          </>
        )}
      </main>

       {!toetsenbord && <TrustBar />}
    </div>
  );
}
